// API hooks for the Drive folder sync feature. Mirrors the pattern of
// useProject + useUpsertProject - one HTTP function per route, paired
// with a React Query hook.
import { useCallback, useEffect, useMemo, useState } from "react";
import { QueryClient, useMutation, useQuery, useQueryClient } from "react-query";
import { QK, queryKeys } from "../queryKeys";

import { useOperationWatcher } from "./useAsyncOperation";
import type { FilesSection, Sighting } from "./useUploadsList";
import { useAgentsHttp } from "../useAgentsHttp";
import { isExtractionInFlight } from "../../utils/extractionInFlight";

// ---------------------------------------------------------------------- //
// Types - keep in lockstep with src/inrange_agents/drive_sync_routes.py and
// drive_sync_sources_routes.py
// ---------------------------------------------------------------------- //
export interface DriveSyncRoot {
  folder_id: string;
  folder_name: string;
  drive_id: string | null;
  excluded_subfolder_ids: string[];
  // A display label: a person, or an agent.
  added_by: string;
  added_at: string | null;
  reason: string;
}

export interface DriveTrackedFile {
  file_id: string;
  name: string;
  drive_path: string;
  added_by: string;
  added_at: string | null;
  reason: string;
}

export interface DriveSyncState {
  authorized_user_id: string;
  authorized_user_email: string;
  // The shared drive everything is added from.
  drive_id: string | null;
  drive_name: string;
  exclude_name_patterns: string[];
  enabled: boolean;
  roots: DriveSyncRoot[];
  // Files the folder editor excluded by id, including ones never ingested.
  excluded_drive_file_ids: string[];
  // Files excluded on the Files tab: Reinstate there brings one back.
  files_tab_excluded_ids: string[];
  // Sorted by drive_path on the BE.
  files: DriveTrackedFile[];
  last_synced_at: string | null;
  // Time of the last *successful* (status "ok") sync, distinct from
  // ``last_synced_at`` (the last attempt, overwritten even on failure). Null
  // until the subscription has completed one clean sync. Mirrors the BE field.
  last_successful_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  last_sync_added_count: number;
  last_sync_changed_count: number;
  last_sync_removed_count: number;
  // True when the last sync's folder walk was too large to fully scan in the
  // scheduled poll's budget - the operator should run a manual sync (larger
  // budget). Only set on a pathologically large tree. Optional: absent on rows
  // written before this field existed.
  last_sync_walk_truncated?: boolean;
  // Paths of subfolders that started syncing in the last sync (BE-capped at
  // 20). Empty on a first sync or when none are new.
  last_sync_new_folders: string[];
  last_sync_added_files: string[];
  last_sync_changed_files: string[];
  last_sync_removed_files: string[];
}

// Mirrors TRACKED_PARENT on the BE: the parent_drive_folder_id of a tracked file.
export const TRACKED_PARENT = "tracked";

// "excluded" is a person's decision or the exclusion settings; null is not covered.
export type DriveCoverage = "root" | "tracked" | "under_root" | "excluded" | null;

export interface DriveBrowseItem {
  file_id: string;
  name: string;
  is_folder: boolean;
  path: string;
  modified_time: string | null;
  coverage: DriveCoverage;
}

export type DriveSearchType = "folder_name" | "file_name" | "file_content";

export interface DriveBrowseResponse {
  searched: string[];
  skipped: string[];
  items: DriveBrowseItem[];
  capped: boolean;
}

export interface SharedDrive {
  id: string;
  name: string;
}

export interface GoogleAuthorization {
  user_id: string;
  user_email: string;
  status: "active" | "failed";
  last_refreshed_at: string | null;
  last_failure_at: string | null;
  last_failure_reason: string | null;
}

export interface GoogleAuthStatus {
  authorization: GoogleAuthorization | null;
  failed_project_ids: string[];
}

export interface DriveFileLeaf {
  id: string;
  name: string;
  size_bytes: number | null;
  mime_type: string;
}

export interface DriveFolderNode {
  id: string;
  name: string;
  path: string;
  children: DriveFolderNode[];
  // The files directly in this folder, only when the tree was asked for them.
  files?: DriveFileLeaf[];
  // Non-folder files DIRECTLY in this folder (not recursive). The picker
  // loads lazily, so a recursive count would force the full walk we're
  // avoiding. Rendered as "Name (N)".
  file_count: number;
  // True when this node has subfolders not yet loaded (it's at the
  // lazy-load depth boundary). The picker shows an expand affordance and
  // fetches the next levels on click.
  has_unloaded_children: boolean;
}

export interface DriveFolderTree {
  root_id: string;
  root_name: string;
  tree: DriveFolderNode;
}

export interface UploadsTreeEntry {
  // The row this tree entry came from. Identifies the PLACE (the Drive file at
  // this path) and is what Exclude / Reinstate / Dismiss act on.
  upload_id: string;
  // The upload that owns the BYTES + extraction + facts. Equal to ``upload_id``
  // on every ordinary row; on a dedup alias (``is_dedup_alias``) it is the fold
  // winner this row's content was deduped onto. Every file-state affordance
  // (extracted content, extraction status, kind, mark out of date) must address
  // THIS id, not ``upload_id``. See
  // docs/Y2026-Q3/design-sighting-collapse-and-dedup-rendering.md.
  effective_upload_id: string;
  // True when this row is a content-dedup fold loser rendered at its own Drive
  // path. The file really is filed there, so it renders as an ordinary file -
  // NOT as the raw "duplicate_content" tombstone it used to (INRT-2560).
  is_dedup_alias: boolean;
  // Active sightings of the EFFECTIVE upload - every place these bytes were
  // seen. Rendered through the shared collapsing SightingList.
  sightings: Sighting[];
  original_name: string;
  content_type: string;
  size_bytes: number | null;
  created_at: string | null;
  kind: string | null;
  extraction_status: string;
  // When a thread the mailbox import is still adding to will be extracted (ISO), or
  // null when it is not held.
  extraction_not_before?: string | null;
  // Char count of the extracted text the agent reads (context cost, not byte
  // size). Null for no-text files / not-yet-computed. Shown inline per row.
  extracted_text_chars?: number | null;
  // Page count of the source document, when it has a page concept at all - NULL
  // for xlsx / plain text, and on rows extracted before it was recorded. Preferred
  // over the char count in the row label: "12 pages" is how an operator thinks
  // about a document's size, where "48,213 chars" is a machine's view of it.
  total_pages?: number | null;
  // The row's lifecycle, straight off ``DBUpload.status`` - so a wrong-site
  // "quarantined" gmail attachment reaches the tree too, not just active /
  // removed. Both non-active states are hidden from the lists and must be left
  // out of any health count, or the count disagrees with what is on screen.
  status: "active" | "removed" | "quarantined";
  removal_reason: string | null;
  removed_at: string | null;
  dismissed_at: string | null;
  last_pulled_at: string | null;
  drive_path: string | null;
  drive_file_id: string | null;
  source: string;
  parent_drive_folder_id: string | null;
  // Gmail provenance: the thread this upload is (NULL otherwise), and the inbox it
  // was read from, which the "open in Gmail" link opens.
  gmail_thread_id: string | null;
  gmail_account_email: string | null;
  gmail_message_count: number | null;
  // ISO timestamp of the most recent message in the thread (the
  // "last message sent" date). Persisted into the upload metadata by
  // the gmail-sync writer on every pull. NULL on legacy rows that
  // predate this field - the FE falls back to ``last_pulled_at`` until
  // the next sync re-pulls the thread.
  gmail_last_message_at: string | null;
  // Project fields an operator marked this file out-of-date on while field
  // conflicts were resolved by hand. Empty for most files.
  out_of_date_fields: string[];
  // True when the file has been file-level marked out-of-date by the
  // operator. Distinct from ``out_of_date_fields.length > 0`` because
  // a file whose extractor doesn't project onto any project field
  // (equipment_datasheet etc.) can still be marked stale - the BE
  // tracks it via a sentinel alignment row that doesn't show up in
  // the per-field list. Either flag flips the file into out-of-date
  // mode in the UI (clock icon + "Mark fresh" button).
  marked_out_of_date: boolean;
  // Per-row Exclude audit. Populated only when ``removal_reason ==
  // "operator_excluded"``. The FE renders "Excluded by <name> on
  // <date>" on the tombstone so the row carries its own provenance.
  excluded_by_email: string | null;
  excluded_by_name: string | null;
  excluded_at: string | null;
  // The section this row renders in (``files_sections.py``), and the thread an
  // attachment nests under, with any logical-thread merge followed to the survivor.
  files_section: FilesSection;
  nested_under_upload_id: string | null;
  // On a THREAD row: the attachments the BE resolved onto it by provenance. Rendered
  // as the union with the thread's own manifest, so a manifest that is stale or
  // mid-rebuild cannot hide a file.
  nested_attachments?: UploadsTreeEntry[];
}

export interface UploadsTreeResponse {
  project_id: string;
  drive_sync_files: UploadsTreeEntry[];
  gmail_sync_files: UploadsTreeEntry[];
  direct_files: UploadsTreeEntry[];
  // Live rows no section claims: a platform bug, shown so it can be reported.
  unaccounted_files: UploadsTreeEntry[];
  // Business attachments of conversations withheld as personal or sign-in mail, one
  // group per conversation; the conversation's subject is never sent.
  withheld_thread_attachments?: {
    withheld_class: string;
    attachments: UploadsTreeEntry[];
  }[];
}

// ---------------------------------------------------------------------- //
// Query keys
// ---------------------------------------------------------------------- //
const KEY_AUTH_STATUS = QK.googleAuthStatus;
const KEY_FOLDER_TREE = QK.driveFolderTree;
const KEY_UPLOADS_TREE = QK.uploadsTree;

// ---------------------------------------------------------------------- //
// Hooks
// ---------------------------------------------------------------------- //
export const useGoogleAuthStatus = (options?: { enabled?: boolean }) => {
  const http = useAgentsHttp();
  return useQuery<GoogleAuthStatus>(
    [KEY_AUTH_STATUS],
    () => http<GoogleAuthStatus>("/google/auth-status", {}),
    {
      enabled: options?.enabled ?? true,
      refetchOnWindowFocus: false,
      // Refresh every 5 min so a token that died mid-session surfaces
      // before the next page load.
      refetchInterval: 5 * 60 * 1000,
    },
  );
};

export const useStartGoogleAuth = () => {
  const http = useAgentsHttp();
  return useMutation<{ auth_url: string; state: string }, Error, {
    redirect_uri: string;
    login_hint?: string;
    // Only the global-data page sets this: it is where a person becomes the
    // account that writes agent documents, and the consent then asks for the
    // permission to create them. The server adds it on its own for an account
    // that already holds that role, so a reconnect from anywhere keeps it.
    for_agent_documents?: boolean;
  }>((body) => http("/google/auth-url", body));
};

export const useGoogleCallback = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    { user_id: string; user_email: string; status: string },
    Error,
    { code: string; state: string; redirect_uri: string }
  >((body) => http("/google/callback", body), {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_AUTH_STATUS]);
    },
  });
};

export const useDisconnectGoogle = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<{ user_id: string; status: string }, Error, void>(
    () => http("/google/disconnect", {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([KEY_AUTH_STATUS]);
      },
    },
  );
};

// Loads the registered root + the top couple of folder levels (the BE
// caps the walk depth and parallelises it, so this stays fast even on a
// folder with hundreds of subfolders). Deeper levels load lazily via
// useLoadDriveFolderChildren when the operator expands a node.
// With a project id the BE browses through the Google account the project's Drive sync
// was set up with, so anyone on the project can preview its folder; with GLOBAL_DATA_TREE
// it browses through global data's connection, confined to its shared drive; with neither
// the viewer's own Google account is used.
export const GLOBAL_DATA_TREE = { globalData: true } as const;
export type DriveTreeScope = string | typeof GLOBAL_DATA_TREE;

// With includeFiles the BE lists one level per request, files included, so each expand
// loads that folder's files and subfolders.
const folderTreeBody = (
  folderId: string | undefined,
  scope: DriveTreeScope | undefined,
  includeFiles: boolean,
) =>
  typeof scope === "object"
    ? { folder_id: folderId, global_data: true, include_files: includeFiles }
    : { folder_id: folderId, project_id: scope, include_files: includeFiles };

export const useDriveFolderTree = (
  folderId?: string,
  enabled = true,
  scope?: DriveTreeScope,
  includeFiles = false,
) => {
  const http = useAgentsHttp();
  return useQuery<DriveFolderTree>(
    [
      KEY_FOLDER_TREE,
      folderId ?? null,
      typeof scope === "object" ? "global" : (scope ?? null),
      includeFiles,
    ],
    () =>
      http<DriveFolderTree>(
        "/projects/drive/folder-tree",
        folderTreeBody(folderId, scope, includeFiles),
      ),
    {
      enabled: Boolean(folderId) && enabled,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    },
  );
};

// Imperative loader for one node, used by the FolderTree picker when the operator
// expands a node flagged has_unloaded_children. Hits the same BE route with the
// clicked folder's id and returns that node: its subfolders, and its files when asked.
export const useLoadDriveFolderChildren = (scope?: DriveTreeScope, includeFiles = false) => {
  const http = useAgentsHttp();
  return useCallback(
    async (folderId: string): Promise<DriveFolderNode> => {
      const resp = await http<DriveFolderTree>(
        "/projects/drive/folder-tree",
        folderTreeBody(folderId, scope, includeFiles),
      );
      return resp.tree;
    },
    [http, scope, includeFiles],
  );
};

export interface SaveDriveSyncBody {
  project_id: string;
  // Only read when setting the link up: the shared drive everything is added from.
  drive_id?: string;
  exclude_name_patterns: string[];
  // A root left out keeps its exclusions, so send only the roots edited here.
  excluded_subfolder_ids_by_root: Record<string, string[]>;
  // Files ticked off and back on; every other file keeps its exclusion.
  exclude_file_ids?: string[];
  include_file_ids?: string[];
  // Files under an excluded folder unticked, so no longer synced on their own.
  untrack_file_ids?: string[];
  // Names of the folders and files ticked or unticked, for the Settings history.
  names?: Record<string, string>;
  enabled: boolean;
}

interface DriveSyncResponse {
  project_id: string;
  drive_sync: DriveSyncState | null;
}

// Written straight into the cached project so every reader of drive_sync flips at
// once; a refetch can land on a replica still serving the old payload.
function applyDriveSyncResponse(queryClient: QueryClient, resp: DriveSyncResponse) {
  queryClient.setQueryData<unknown>(queryKeys.projectGet(resp.project_id), (prev: unknown) => {
    if (!prev || typeof prev !== "object") return prev;
    return { ...(prev as Record<string, unknown>), drive_sync: resp.drive_sync };
  });
  queryClient.invalidateQueries(queryKeys.projectGet(resp.project_id));
  queryClient.invalidateQueries([KEY_UPLOADS_TREE, resp.project_id]);
  queryClient.invalidateQueries([QK.driveBrowse, resp.project_id]);
}

// Retry a transient 5xx up to 3 extra times with exponential backoff.
// The qa-agents account's Lambda concurrent-execution quota is 10
// (AWS default for new accounts); when agent runs + extraction +
// async_status polls + the drive-sync poll converge, spikes can hit
// the cap and API Gateway returns a 503
// ``{"message":"Service Unavailable"}`` without ever invoking the
// Lambda. The first iteration of this retry tried once after 1.5s -
// a single 5+ second burst (Noaa hit one mid-extraction) blew right
// past it and re-surfaced the 503 to the operator. 3 attempts at
// 1.5s / 3s / 6s rides through windows up to ~10.5s of contention,
// which covers every account-quota spike we've observed in
// CloudWatch concurrency metrics on this env. 4xx (auth, validation,
// not-found) is still rethrown immediately.
export async function retryOn5xx<T>(
  http: (path: string, body?: unknown) => Promise<T>,
  path: string,
  body: unknown,
): Promise<T> {
  const delays = [1500, 3000, 6000];
  let lastError: unknown;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    try {
      return await http(path, body);
    } catch (e) {
      const err = e as { status?: number };
      const transient = err.status !== undefined && err.status >= 500 && err.status < 600;
      if (!transient || attempt === delays.length) {
        throw e;
      }
      lastError = e;
      await new Promise((r) => setTimeout(r, delays[attempt]));
    }
  }
  // Unreachable - either the loop returned or threw - but appeases TS.
  throw lastError;
}

export const useSaveDriveSync = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<DriveSyncResponse, Error, SaveDriveSyncBody>(
    (body) => retryOn5xx<DriveSyncResponse>(http, "/projects/drive/save-settings", body),
    { onSuccess: (resp) => applyDriveSyncResponse(queryClient, resp) },
  );
};

export interface DriveBrowseWhere {
  folderId: string | null;
  query: string;
  searchType: DriveSearchType;
}

// Where an add dialog browses from: a project's connected shared drive, or global data's.
export interface DriveBrowseSource {
  queryKey: readonly unknown[];
  browse: (where: DriveBrowseWhere) => Promise<DriveBrowseResponse>;
}

export const useProjectDriveBrowseSource = (projectId: string): DriveBrowseSource => {
  const http = useAgentsHttp();
  return useMemo(
    () => ({
      queryKey: queryKeys.driveBrowse(projectId),
      browse: (where) =>
        http<DriveBrowseResponse>("/projects/drive/browse", {
          project_id: projectId,
          folder_id: where.folderId ?? undefined,
          query: where.query,
          search_type: where.searchType,
        }),
    }),
    [http, projectId],
  );
};

// Lists a folder (folderId), searches (query), or with neither lists the connected shared
// drive's top level, all through the authorizer's Google access so any viewer can browse.
export const useDriveBrowse = (
  source: DriveBrowseSource,
  where: DriveBrowseWhere,
  enabled: boolean,
) =>
  useQuery<DriveBrowseResponse>(
    [...source.queryKey, where.folderId, where.query, where.searchType],
    () => source.browse(where),
    { enabled, refetchOnWindowFocus: false, staleTime: 60 * 1000 },
  );

// The shared drives the signed-in user's own Google account can open, sorted by name.
export const useSharedDrives = (enabled: boolean) => {
  const http = useAgentsHttp();
  return useQuery<{ drives: SharedDrive[] }>(
    [QK.googleSharedDrives],
    () => http<{ drives: SharedDrive[] }>("/google/drive/shared-drives", {}),
    { enabled, refetchOnWindowFocus: false, staleTime: 60 * 1000 },
  );
};

// operation_key is a drive_sync_now LRO for just the new items, or null when every
// item was already covered.
export const useAddDriveSources = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    DriveSyncResponse & { operation_key: string | null },
    Error,
    { project_id: string; folder_ids: string[]; file_ids: string[]; actor_name: string }
  >((body) => http("/projects/drive/add-sources", body), {
    onSuccess: (resp) => applyDriveSyncResponse(queryClient, resp),
  });
};

export const useRemoveDriveRoot = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    DriveSyncResponse,
    Error,
    { project_id: string; folder_id: string; actor_name: string }
  >((body) => http("/projects/drive/remove-root", body), {
    onSuccess: (resp) => applyDriveSyncResponse(queryClient, resp),
  });
};

export const useUntrackDriveFile = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    DriveSyncResponse,
    Error,
    { project_id: string; file_id: string; actor_name: string }
  >((body) => http("/projects/drive/untrack-file", body), {
    onSuccess: (resp) => applyDriveSyncResponse(queryClient, resp),
  });
};

// Moves the link onto the caller's Google account, keeping its folders, files and
// exclusions - unlike disconnecting and setting it up again.
export const useUseMyAccountForDriveSync = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<DriveSyncResponse, Error, { project_id: string }>(
    (body) => http<DriveSyncResponse>("/projects/drive/use-my-account", body),
    { onSuccess: (resp) => applyDriveSyncResponse(queryClient, resp) },
  );
};

export const useDisconnectDriveSync = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    { project_id: string; drive_sync: null },
    Error,
    { project_id: string }
  >((body) => http("/projects/drive/disconnect", body), {
    onSuccess: (_resp, vars) => {
      queryClient.invalidateQueries(queryKeys.projectGet(vars.project_id));
      queryClient.invalidateQueries([KEY_UPLOADS_TREE, vars.project_id]);
    },
  });
};

// Drive sync-now is a tier-2 LRO. The route
// stages an operation row + fires the async worker and returns 202 + an
// operation_key in <1s, so the heavy per-file extraction (~30s/file) runs
// on the 15-min worker instead of blowing the 29s API Gateway window (the
// old synchronous version 504'd the browser with no feedback while the
// removal/extraction ran to completion server-side). The caller drives
// `useAsyncOperation` against the returned operation_key, then invalidates
// the uploads tree + project query on completion so removed/added files
// surface without a manual refresh.
export const useTriggerDriveSync = () => {
  const http = useAgentsHttp();
  return useMutation<
    {
      operation_key: string;
      status: string;
      project_id: string;
    },
    Error,
    { project_id: string }
  >((body) => http("/projects/drive/sync-now", body));
};

// On-mount re-attach: returns the operation_key of a drive_sync_now LRO
// already running for this project, or null. The settings card uses it to
// resume its progress poll when the operator navigates back mid-sync - the
// op key otherwise lived only in component state and was lost on unmount,
// so the bar vanished even though the worker kept running to completion
// server-side. Stale ops are reported as not-running by the BE (principle
// 16). `enabled` is gated off once we're already tracking an op key so we
// stop asking.
export const useActiveDriveSync = (projectId: string, enabled: boolean) => {
  const http = useAgentsHttp();
  return useQuery<{ operation_key: string | null }>(
    queryKeys.driveActiveSync(projectId),
    () =>
      http<{ operation_key: string | null }>("/projects/drive/active-sync", {
        project_id: projectId,
      }),
    {
      enabled: Boolean(projectId) && enabled,
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );
};

/** One project's Drive "Sync now" operation, shared by every surface that starts or
 * shows one: re-attaches to a sync already running when the surface mounts, and when
 * it stops (however it stops) refreshes the tree and the project and reports a
 * failure. */
export const useDriveSyncOperation = (projectId: string) => {
  const queryClient = useQueryClient();
  const [operationKey, setOperationKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activeSync = useActiveDriveSync(projectId, operationKey === null);
  const running = activeSync.data?.operation_key;
  useEffect(() => {
    if (running && operationKey === null) setOperationKey(running);
  }, [running, operationKey]);
  const operation = useOperationWatcher(operationKey, (record) => {
    queryClient.invalidateQueries(queryKeys.uploadsTree(projectId));
    queryClient.invalidateQueries(queryKeys.projectGet(projectId));
    if (record.status === "failed") setError(record.error ?? "unknown error");
    // Dropped so re-enabling the re-attach query refetches rather than briefly
    // re-adopting the key that just finished.
    queryClient.removeQueries(queryKeys.driveActiveSync(projectId));
    setOperationKey(null);
  });
  const track = useCallback((key: string) => {
    setError(null);
    setOperationKey(key);
  }, []);
  return {
    inFlight: operationKey !== null,
    progress: operationKey !== null ? (operation.data?.progress ?? null) : null,
    error,
    setError,
    track,
  };
};

export const useUploadsTree = (projectId?: string) => {
  const http = useAgentsHttp();
  return useQuery<UploadsTreeResponse>(
    [KEY_UPLOADS_TREE, projectId ?? null],
    () => http<UploadsTreeResponse>("/uploads/tree", {
      project_id: projectId,
    }),
    {
      enabled: Boolean(projectId),
      refetchOnWindowFocus: false,
      // Drive sync commits upload rows incrementally as files land,
      // but the /sync-now route doesn't return until every file's
      // extraction has run (synchronous, ~30s/file). With 13 files
      // that's 6+ minutes of "spinner" the operator stares at while
      // their rows are actually appearing in the DB. Refetching the
      // tree every 5s while any row is mid-extraction OR while the
      // tree is empty (sync probably in flight) closes that gap -
      // files appear as they get committed instead of all-at-once at
      // the end. Polling stops the moment every row has settled.
      refetchInterval: (data) => {
        if (!data) return 5000;
        const allFiles = [
          ...data.drive_sync_files,
          ...data.gmail_sync_files,
          ...data.direct_files,
          ...data.unaccounted_files,
          ...(data.withheld_thread_attachments ?? []).flatMap((g) => g.attachments),
        ];
        if (allFiles.length === 0) return 5000;
        const anyInFlight = allFiles.some((f) =>
          isExtractionInFlight(f.extraction_status),
        );
        return anyInFlight ? 5000 : false;
      },
    },
  );
};

export const useDismissUpload = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    { upload_id: string; dismissed: boolean },
    Error,
    { upload_id: string; project_id: string }
  >((body) => http("/uploads/dismiss", { upload_id: body.upload_id }), {
    onSuccess: (_resp, vars) => {
      queryClient.invalidateQueries([KEY_UPLOADS_TREE, vars.project_id]);
    },
  });
};

export const useExcludeDriveFile = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    { project_id: string; drive_sync: DriveSyncState | null },
    Error,
    { project_id: string; upload_id: string; actor_name: string }
  >((body) => http("/projects/drive/exclude-file", body), {
    onSuccess: (_resp, vars) => {
      queryClient.invalidateQueries([KEY_UPLOADS_TREE, vars.project_id]);
      queryClient.invalidateQueries(queryKeys.projectGet(vars.project_id));
    },
  });
};

export const useReinstateDriveFile = () => {
  const http = useAgentsHttp();
  const queryClient = useQueryClient();
  return useMutation<
    { project_id: string; drive_sync: DriveSyncState | null },
    Error,
    { project_id: string; upload_id: string }
  >((body) => http("/projects/drive/reinstate-file", body), {
    onSuccess: (_resp, vars) => {
      queryClient.invalidateQueries([KEY_UPLOADS_TREE, vars.project_id]);
      queryClient.invalidateQueries(queryKeys.projectGet(vars.project_id));
    },
  });
};
